<template>
  <div id="afdc-station-details-map"></div>
</template>

<script>
import MapMixin from '@/lib/MapMixin';
import mapboxgl from 'mapbox-gl/dist/mapbox-gl-unminified';

export default {
  props: ['station'],

  mixins: [
    MapMixin,
  ],

  beforeCreate() {
    this.smallMarkers = [];

    this.mapContainer = document.createElement('div');
    this.mapContainer.style.height = '100%';
    this.mapContainer.style.width = '100%';
    this.map = new mapboxgl.Map({
      attributionControl: true,
      minZoom: 1,
      maxZoom: 20,
      container: this.mapContainer,
    });

    this.map.addControl(new mapboxgl.NavigationControl({
      showZoom: true,
      showCompass: false,
    }), 'bottom-right');
  },

  created() {
    // Zoom to the marker. We do this as early as possible, and before the map
    // is actually rendered, to prevent the map from trying to fetch the base
    // map data for the initial zoom level, and then panning to this specific
    // area and having to fetch the base map data again.
    this.zoomToMarker();

    this.fetchMapStyle();
  },

  mounted() {
    this.$nextTick(() => {
      this.initMap();
    });
  },

  beforeDestroy() {
    if (this.map) {
      this.map.remove();
    }
  },

  computed: {
    stationGeojson() {
      return this.$store.getters.stationDetailsGeojson;
    },
  },

  watch: {
    stationGeojson: {
      immediate: true,
      handler: 'drawStationPoint',
    },
  },

  methods: {
    addImageDataToMap() {
      const pixelRatio = this.$store.getters.mapPixelRatio;
      const imageData = this.$store.getters.mapImageData;

      // Add just the base fuel markers (we don't need all the lettered
      // variants for this small map).
      const fuels = this.$store.getters.mapImageFuels;
      fuels.forEach((fuel) => {
        const pinId = `pin-${fuel}`;
        this.map.addImage(pinId, imageData[pinId], { pixelRatio });
      });
    },

    initMap() {
      document.getElementById('afdc-station-details-map').appendChild(this.mapContainer);
      if (this.map) {
        this.map.resize();
      }
    },

    zoomToMarker() {
      if (this.map && this.stationGeojson && this.stationGeojson.features && this.stationGeojson.features[0]) {
        this.map.jumpTo({
          center: this.stationGeojson.features[0].geometry.coordinates,
          zoom: 14,
        });

        // Pan the map slightly, to better visually center the marker (since
        // this map isn't very tall, and the marker takes up a decent chunk
        // of the height).
        this.map.panBy([0, -25], {
          animate: false,
        });
      }
    },

    addDataToMap() {
      this.addImageDataToMap();
      this.map.addSource('station-marker-source', {
        type: 'geojson',
        data: this.stationGeojson,
      });
      this.mapSource = this.map.getSource('station-marker-source');

      this.map.addLayer({
        id: 'station-marker-layer',
        type: 'symbol',
        source: 'station-marker-source',
        layout: {
          'icon-image': 'pin-{fuel}',
          'icon-size': 1.0,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-anchor': 'bottom',
        },
      });
      this.mapLayer = this.map.getLayer('station-marker-layer');

      this.zoomToMarker();

      this.mapDataLoaded = true;
    },

    drawStationPoint() {
      if (this.mapSource) {
        this.mapSource.setData(this.stationGeojson);
        this.zoomToMarker();
      }
    },
  },
};
</script>

<style lang="scss">
#station-details-map {
  width: 100%;
  height: 170px;
  margin-bottom: 50px;

  .mapboxgl-ctrl-group > button {
    width: 24px;
    height: 24px;
    text-align: center;

    .mapboxgl-ctrl-compass-arrow {
      margin: 2px;
    }
  }
}
</style>
